import { ArrayHelpers, Field, FieldArray, getIn } from 'formik';
import React from 'react';
import Stack, { FormStack, contentStackSpacing } from '../../v2BaseComponents/Stack';
import { getSpacing } from '../../../utils/spacing';
import Text from '../../v2BaseComponents/Text';
import { getIntl } from '../../../utils/intl';
import IconButton from '../../v2BaseComponents/IconButton';
import { useIsDisabledByParent } from '../../v2BaseComponents/IsDisabledProvider';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { required } from '../../../utils/validators';
import Divider from '../../v2BaseComponents/Divider';
import Icon from '../../v2BaseComponents/Icon';
import Button from '../../v2BaseComponents/Button';
import { useTempFilePicker } from '../../v2BaseComponents/ImagePicker/TempFilePicker';
import { FormikFieldProps } from '../../v2BaseComponents/Inputs/FormikInputs';
import { colors } from '../../../utils/theme';
import { APITypes, Dynamo } from '../../../primary-record-types';

const intl = getIntl('invitation-definition-form-fields-checkboxes', {
    name: 'Name',
    removeFile: 'Remove File',
    uploadFile: 'Upload File',
});

const NEW_FILE_ID = 'new-file';

export type FileFieldValue = {
    name: string;
    fileId: string;
    tempFileId?: string;
    contentType?: string;
};

export const mapAPIFileToFormValue = (files: Dynamo.MedicalProfileDirectiveFile[]): FileFieldValue[] => {
    return files.map((file) => ({
        name: file.name,
        fileId: file.fileId,
    }));
};

export const mapFormValueToAPIFile = (
    files: FileFieldValue[],
    initialValues: Dynamo.MedicalProfileDirectiveFile[],
): APITypes.medicalProfileService.ChangeMedicalProfileDirectiveFile[] => {
    const deletedFiles = initialValues.filter((initialFile) => {
        return !files.some((file) => file.fileId === initialFile.fileId);
    });
    const createdFiles = files.filter((_) => _.fileId === NEW_FILE_ID);
    const modifiedFiles = files.filter((_) => _.fileId !== NEW_FILE_ID && _.tempFileId);
    const notModifiedFiles = files.filter((_) => _.fileId !== NEW_FILE_ID && !_.tempFileId);
    return [
        ...deletedFiles.map<APITypes.medicalProfileService.DeleteMedicalProfileDirectiveFile>((_) => ({
            type: 'delete',
            fileId: _.fileId,
        })),
        ...createdFiles.map<APITypes.medicalProfileService.CreateMedicalProfileDirectiveFile>((_) => ({
            type: 'create',
            name: _.name,
            tempFileId: _.tempFileId!,
            contentType: _.contentType!,
        })),
        ...modifiedFiles.map<APITypes.medicalProfileService.UpdateMedicalProfileDirectiveFile>((_) => ({
            type: 'update',
            fileId: _.fileId,
            name: _.name,
            contentType: _.contentType!,
            tempFileId: _.tempFileId!,
        })),
        ...notModifiedFiles.map<APITypes.medicalProfileService.NotModifiedMedicalProfileDirectiveFile>((_) => ({
            type: 'not-modified',
            fileId: _.fileId,
            name: _.name,
        })),
    ];
};

export type FilesFieldOwnProps = {
    isDisabled: boolean;
    values: FileFieldValue[];
    baseFieldName: string;
    sectionTitle: string;
    getFileSectionTitle: (index: number) => string;
    removeDisabled?: boolean;
    addButtonAfterFileIndex?: number;
};

export type FormInputSectionOwnProps = {
    children: React.ReactNode;
    title: string;
    removeTestID: string;
    onRemove: () => void;
    isDisabled: boolean;
    removeDisabled: boolean;
};

export type FormInputSectionProps = FormInputSectionOwnProps;

const FormInputSection: React.FC<FormInputSectionProps> = (props) => {
    return (
        <Stack vertical fullWidth style={{ backgroundColor: colors.Background, borderRadius: 8 }} padding={4} space={2}>
            <Stack horizontal fullWidth justify="space-between" align={'center'} space={contentStackSpacing}>
                <Text variant="body-1">{props.title}</Text>
                {props.removeDisabled ? null : (
                    <IconButton
                        onPress={props.onRemove}
                        variant="transparent"
                        icon="X"
                        isDisabled={props.isDisabled}
                        testID={props.removeTestID}
                    />
                )}
            </Stack>
            <FormStack>{props.children}</FormStack>
        </Stack>
    );
};

export type FilesFieldProps = FilesFieldOwnProps;

const ExistingFileDisplay: React.FC<{
    onClear: () => void;
    isDisabled: boolean;
    index: number;
}> = (props) => (
    <Stack vertical fullWidth justify="center" align="center">
        <Icon name="FileText" size="selected-file-icon" testID="file-picker-display" />
        <Button
            variant="text-dark"
            onPress={props.onClear}
            isDisabled={props.isDisabled}
            title={intl('removeFile')}
            icon="Trash2"
            testID={`delete-file-${props.index}`}
        />
    </Stack>
);

type OnUploadArgs = {
    tempFileId: string;
    name: string;
    contentType: string;
};
const UploadFileInput: React.FC<{ onUpload: (args: OnUploadArgs) => void; isDisabled: boolean; error?: string }> = (
    props,
) => {
    const { isLoadingFile, onPress, E2EInput } = useTempFilePicker({
        onPick: (value) => {
            if (value?.tempFileId) {
                props.onUpload({
                    tempFileId: value.tempFileId,
                    contentType: value.contentType,
                    name: value.name,
                });
            }
        },
    });

    return (
        <Stack vertical fullWidth justify="center" align="center">
            {E2EInput}
            <Button
                variant="secondary"
                onPress={onPress}
                isDisabled={props.isDisabled || isLoadingFile}
                title={intl('uploadFile')}
                icon="Upload"
            />
            {props.error ? (
                <Text variant="input-error" style={{ marginTop: getSpacing(1) }}>
                    {props.error}
                </Text>
            ) : null}
        </Stack>
    );
};

const FormikFieldUploadFileInput: React.FC<
    FormikFieldProps<{}> & { isDisabled: boolean; baseName: string; name: string }
> = (props) => {
    const validationError = getIn(props.form.errors, props.field.name);
    const touched = getIn(props.form.touched, props.field.name);
    const error = touched && validationError ? validationError : null;

    return (
        <UploadFileInput
            onUpload={(tempFileId) => {
                props.form.setFieldValue(`${props.baseName}.tempFileId`, tempFileId.tempFileId);
                props.form.setFieldValue(`${props.baseName}.name`, tempFileId.name);
                props.form.setFieldValue(`${props.baseName}.contentType`, tempFileId.contentType);
            }}
            isDisabled={props.isDisabled}
            error={error}
        />
    );
};

const FileField: React.FC<{
    index: number;
    onRemove: () => void;
    isDisabled: boolean;
    value: FileFieldValue;
    onChange: (value: FileFieldValue) => void;
    baseFieldName: string;
    getFileSectionTitle: (index: number) => string;
    removeDisabled: boolean;
}> = (props) => {
    return (
        <FormInputSection
            title={props.getFileSectionTitle(props.index)}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            removeTestID={`remove-file-${props.index}`}
            removeDisabled={props.removeDisabled}
        >
            {props.value.fileId !== NEW_FILE_ID || props.value.tempFileId ? (
                <ExistingFileDisplay
                    onClear={() =>
                        props.onChange({
                            ...props.value,
                            tempFileId: '',
                            contentType: '',
                            fileId: NEW_FILE_ID,
                        })
                    }
                    isDisabled={props.isDisabled}
                    index={props.index}
                />
            ) : (
                <Field
                    name={`${props.baseFieldName}.${props.index}.tempFileId`}
                    baseName={`${props.baseFieldName}.${props.index}`}
                    component={FormikFieldUploadFileInput}
                    isDisabled={props.isDisabled}
                    validate={required}
                    isRequired
                />
            )}
            <Field
                name={`${props.baseFieldName}.${props.index}.name`}
                component={FormikInputs.Input}
                isRequired
                validate={required}
                title={intl('name')}
            />
        </FormInputSection>
    );
};

export const getAddedFileDefaultValue = (): FileFieldValue => ({
    name: '',
    tempFileId: undefined,
    fileId: NEW_FILE_ID,
});

const FilesField: React.FC<FilesFieldProps> = (props) => {
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    const addButtonAFterFileIndex = props.addButtonAfterFileIndex || 0;
    const preFileValues = props.values.slice(0, addButtonAFterFileIndex);
    const postFileValues = props.values.slice(addButtonAFterFileIndex);

    return (
        <FieldArray name={props.baseFieldName}>
            {(arrayHelpers: ArrayHelpers<FileFieldValue[]>) => (
                <Stack
                    vertical
                    space={contentStackSpacing}
                    fullWidth
                    style={{ marginTop: getSpacing(2.5), marginBottom: getSpacing(2.5) }}
                >
                    {preFileValues.map((image, index) => (
                        <FileField
                            removeDisabled={!!props.removeDisabled}
                            key={index}
                            onRemove={() => arrayHelpers.remove(index)}
                            index={index}
                            isDisabled={props.isDisabled}
                            value={image}
                            onChange={(value) => {
                                arrayHelpers.replace(index, value);
                            }}
                            baseFieldName={props.baseFieldName}
                            getFileSectionTitle={props.getFileSectionTitle}
                        />
                    ))}
                    <Stack horizontal justify={'space-between'} align="center" fullWidth>
                        <Text variant="h5">{props.sectionTitle}</Text>
                        <IconButton
                            testID="add-file-button"
                            icon="Plus"
                            variant="transparent"
                            onPress={() => {
                                arrayHelpers.push(getAddedFileDefaultValue());
                            }}
                            isDisabled={isDisabled}
                        />
                    </Stack>
                    {postFileValues.map((image, index) => (
                        <FileField
                            removeDisabled={!!props.removeDisabled}
                            key={index}
                            onRemove={() => arrayHelpers.remove(index + preFileValues.length)}
                            index={index + preFileValues.length}
                            isDisabled={props.isDisabled}
                            value={image}
                            onChange={(value) => {
                                arrayHelpers.replace(index + preFileValues.length, value);
                            }}
                            baseFieldName={props.baseFieldName}
                            getFileSectionTitle={props.getFileSectionTitle}
                        />
                    ))}
                    {props.values.length ? <Divider horizontal /> : null}
                </Stack>
            )}
        </FieldArray>
    );
};

export default React.memo(FilesField);
